import { useEffect, useRef } from "react";
import { MAX_LINK, MOUSE_RADIUS, drawScale, linkAlpha, makeParticles, particleCount, pointerPush, stepParticle } from "../heroFxMath";

const COLORS = { cyan: "34, 211, 238", violet: "168, 85, 247" };

// The living background of every hero: glowing aurora blobs, a slowly drifting grid, a network of
// drifting dots that join up and shy away from the pointer, and a light sweep. It sits behind the
// hero's content, ignores clicks, pauses when it is off screen or the tab is hidden, and is a still
// picture for people who ask their device for reduced motion.
export default function HeroFX({ dots = true }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!dots || !canvas || !canvas.getContext) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;
    const host = canvas.parentElement;
    const section = canvas.closest("section") || host;
    const reduced = typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let width = 0;
    let height = 0;
    let particles = [];
    let pointer = null;
    let raf = 0;
    let last = 0;
    let visible = true;

    const resize = () => {
      const rect = host.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const scale = drawScale(window.devicePixelRatio);
      const changed = Math.abs(rect.width - width) > 1 || Math.abs(rect.height - height) > 1;
      width = rect.width;
      height = rect.height;
      canvas.width = Math.round(width * scale);
      canvas.height = Math.round(height * scale);
      ctx.setTransform(scale, 0, 0, scale, 0, 0);
      if (changed || !particles.length) particles = makeParticles(particleCount(width, height), width, height);
      draw();
    };

    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      const shown = particles.map((p) => {
        const push = pointerPush(p, pointer);
        return { ...p, dx: p.x + push.dx, dy: p.y + push.dy };
      });
      ctx.lineWidth = 1;
      for (let i = 0; i < shown.length; i += 1) {
        for (let j = i + 1; j < shown.length; j += 1) {
          const a = shown[i];
          const b = shown[j];
          const alpha = linkAlpha(Math.hypot(a.dx - b.dx, a.dy - b.dy), MAX_LINK);
          if (alpha <= 0.01) continue;
          ctx.strokeStyle = `rgba(${COLORS[a.hue]}, ${Math.min(0.6, alpha * 1.1)})`;
          ctx.beginPath();
          ctx.moveTo(a.dx, a.dy);
          ctx.lineTo(b.dx, b.dy);
          ctx.stroke();
        }
        // a line from the pointer to the dots it is near, so the network feels attached to the cursor
        if (pointer) {
          const d = Math.hypot(shown[i].dx - pointer.x, shown[i].dy - pointer.y);
          const alpha = linkAlpha(d, MOUSE_RADIUS) * 0.9;
          if (alpha > 0.02) {
            ctx.strokeStyle = `rgba(${COLORS.cyan}, ${alpha})`;
            ctx.beginPath();
            ctx.moveTo(shown[i].dx, shown[i].dy);
            ctx.lineTo(pointer.x, pointer.y);
            ctx.stroke();
          }
        }
      }
      shown.forEach((p) => {
        ctx.fillStyle = `rgba(${COLORS[p.hue]}, 0.95)`;
        ctx.shadowColor = `rgba(${COLORS[p.hue]}, 0.9)`;
        ctx.shadowBlur = 12;
        ctx.beginPath();
        ctx.arc(p.dx, p.dy, p.r, 0, Math.PI * 2);
        ctx.fill();
      });
      ctx.shadowBlur = 0;
    };

    const frame = (now) => {
      raf = 0;
      if (!visible || document.hidden) return;
      const dt = Math.min(0.05, (now - (last || now)) / 1000);
      last = now;
      particles = particles.map((p) => stepParticle(p, dt, width, height));
      draw();
      raf = requestAnimationFrame(frame);
    };
    const start = () => {
      if (reduced || raf || !visible || document.hidden) return;
      last = 0;
      raf = requestAnimationFrame(frame);
    };
    const stop = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
    };

    const onMove = (e) => {
      const rect = host.getBoundingClientRect();
      pointer = { x: e.clientX - rect.left, y: e.clientY - rect.top };
      if (reduced) draw();
    };
    const onLeave = () => {
      pointer = null;
      if (reduced) draw();
    };
    const onVisibility = () => (document.hidden ? stop() : start());

    resize();
    start();
    section.addEventListener("pointermove", onMove);
    section.addEventListener("pointerleave", onLeave);
    document.addEventListener("visibilitychange", onVisibility);
    const resizeObserver = typeof ResizeObserver !== "undefined" ? new ResizeObserver(resize) : null;
    if (resizeObserver) resizeObserver.observe(host);
    const intersectionObserver = typeof IntersectionObserver !== "undefined"
      ? new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; if (visible) start(); else stop(); })
      : null;
    if (intersectionObserver) intersectionObserver.observe(host);

    return () => {
      stop();
      section.removeEventListener("pointermove", onMove);
      section.removeEventListener("pointerleave", onLeave);
      document.removeEventListener("visibilitychange", onVisibility);
      if (resizeObserver) resizeObserver.disconnect();
      if (intersectionObserver) intersectionObserver.disconnect();
    };
  }, [dots]);

  return (
    <div className="hero-fx absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
      <div className="floating-light absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-accent/20 blur-[120px]" style={{ animationDuration: "18s" }} />
      <div className="floating-light absolute -bottom-40 -right-20 w-[500px] h-[500px] rounded-full bg-accent-light/10 blur-[120px]" style={{ animationDuration: "22s", animationDelay: "2s" }} />
      <div className="floating-light absolute top-1/3 left-1/2 w-[350px] h-[350px] rounded-full bg-purple-500/10 blur-[100px]" style={{ animationDuration: "26s", animationDelay: "4s" }} />
      <div className="floating-light absolute bottom-10 left-10 w-[300px] h-[300px] rounded-full bg-accent-light/10 blur-[100px]" style={{ animationDuration: "20s", animationDelay: "1s" }} />
      <div className="hero-fx-grid absolute inset-0" />
      <div className="hero-fx-beam absolute inset-y-0" />
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />
      <div className="hero-fx-edge absolute inset-x-0 bottom-0" />
    </div>
  );
}
